<script setup lang="ts">
import type { StatRowSection, Theme } from './report-typing';

import { useUpdatedReportStore } from '@src/store/report-store';

import { computed } from 'vue';

import FieldRenderer from './FieldRenderer.vue';

defineProps<{
  section: StatRowSection;
}>();

const reportStore = useUpdatedReportStore();

const theme = computed<Theme | null>(() => {
  const loadedTemplate = reportStore.getTemplate();
  return loadedTemplate?.theme ?? null;
});

const headerStyle = computed(() => {
  if (!theme.value) return {};
  return {
    borderBottom: `1px solid ${theme.value.colors.border}`,
    paddingBottom: '4px',
    marginBottom: `${theme.value.spacing.fieldGap / 2}px`,
  };
});

const titleStyle = computed(() => {
  if (!theme.value) return {};
  const sl = theme.value.fonts.sectionLabel;
  return {
    fontFamily: sl.family,
    fontSize: `${sl.size}px`,
    fontWeight: sl.weight,
    color: theme.value.colors.text,
    textTransform: sl.uppercase ? 'uppercase' : 'none',
    letterSpacing: sl.letterSpacing ? `${sl.letterSpacing}px` : undefined,
  };
});

const mutedStyle = computed(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.label.family,
    fontSize: `${theme.value.fonts.label.size}px`,
    color: theme.value.colors.muted,
  };
});

const fieldsGapStyle = computed(() => {
  if (!theme.value) return {};
  return {
    gap: `${theme.value.spacing.fieldGap}px`,
  };
});
</script>

<template>
  <div class="stat-row-section">
    <!-- Title -->
    <div v-if="section.title" class="stat-row-header" :style="headerStyle">
      <span class="stat-row-title" :style="titleStyle">
        {{ section.title }}
      </span>
      <span v-if="section.unit" class="stat-row-unit" :style="mutedStyle">
        ({{ section.unit }})
      </span>
    </div>

    <!-- Stat fields rendered horizontally -->
    <div class="stat-row-fields" :style="fieldsGapStyle">
      <div v-for="stat in section.stats" :key="stat.key" class="stat-item">
        <FieldRenderer :field="stat" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.stat-row-section {
  display: flex;
  flex-direction: column;
}

.stat-row-header {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.stat-row-title {
  font-size: 11px;
  font-weight: 600;
}

.stat-row-unit {
  font-size: 9px;
  color: #888;
}

.stat-row-fields {
  display: flex;
  gap: 10px;
}

.stat-item {
  flex: 1;
  min-width: 0;
}
</style>
